import { OcrInferenceResult } from '@/features/InvoiceReport/Edit/components/type';

import { TotalAmountPaid } from '@/components/ReportForm';
import { HeaderProps } from '@/components/ReportForm/AmountTablesMenu/Header';
import { RowsProps } from '@/components/ReportForm/AmountTablesMenu/Rows';
import { EXCISE_DISPLAY_ORDER, EXCISE_LABELS } from '@/utils/excise';
import classNames from 'classnames';
import { useMemo } from 'react';
import styles from './PaymentDetailSummary.module.scss';

const cx = classNames.bind(styles);
export type PaymentDetailSummaryProps = {
  detailsTotalAmountValue?: number;
  taxAmountValue?: number;
  totalAmountValue?: number;
  withholdingIncomeTaxValue?: number;
  hasWithholdingIncomeTax?: boolean;
  subtotal10?: OcrInferenceResult['subtotal10'];
  subtotal8?: OcrInferenceResult['subtotal8'];
  subtotalTaxFree?: OcrInferenceResult['subtotalTaxFree'];
};

export const PaymentDetailSummary = ({
  detailsTotalAmountValue,
  taxAmountValue,
  totalAmountValue,
  withholdingIncomeTaxValue,
  hasWithholdingIncomeTax,
  subtotal10,
  subtotal8,
  subtotalTaxFree,
}: PaymentDetailSummaryProps) => {
  const summaryTableRows: {
    [key: string]: {
      key: string;
      name: string;
      totalValue?: number;
      totalValueWithoutTax?: number;
      exciseValue?: number;
    };
  } = {};

  const subtotalValues: {
    key: keyof typeof EXCISE_LABELS;
    value:
      | OcrInferenceResult['subtotal10']
      | OcrInferenceResult['subtotal8']
      | OcrInferenceResult['subtotalTaxFree'];
  }[] = [
    { key: '10percent', value: subtotal10 },
    { key: '8percent', value: subtotal8 },
    { key: '0percent', value: subtotalTaxFree },
  ];

  subtotalValues.forEach(({ key, value }) => {
    if (
      value &&
      (value.totalValue || value.totalValueWithoutTax || value.exciseValue)
    ) {
      summaryTableRows[key] = {
        key: key,
        name: EXCISE_LABELS[key],
        totalValueWithoutTax: value.totalValueWithoutTax || 0,
        exciseValue: value.exciseValue || 0,
        totalValue: value.totalValue || 0,
      };
    }
  });

  const exciseHeaders: HeaderProps['cells'] = useMemo(
    () => [
      {
        value: '税率別内訳',
      },
      {
        value: '小計（税抜）',
        textRight: true,
        size: 'sm',
      },
      {
        value: '消費税額',
        textRight: true,
        size: 'sm',
      },
      {
        value: '小計（税込）',
        textRight: true,
        size: 'sm',
      },
    ],
    []
  );

  // type guard function
  const isValidSummaryTableRow = (
    summaryTableRow: (typeof summaryTableRows)[string] | undefined
  ): summaryTableRow is NonNullable<(typeof summaryTableRows)[string]> => {
    return summaryTableRow !== undefined && summaryTableRow !== null;
  };

  const exciseRows: RowsProps['rows'] = EXCISE_DISPLAY_ORDER.map(
    (key) => summaryTableRows[key]
  )
    .filter(isValidSummaryTableRow)
    .map((summaryTableRow) => ({
      cells: [
        { value: summaryTableRow.name },
        {
          value: summaryTableRow.totalValueWithoutTax || 0,
          textRight: true,
          size: 'sm',
        },
        {
          value: summaryTableRow.exciseValue || 0,
          textRight: true,
          size: 'sm',
        },
        {
          value: summaryTableRow.totalValue || 0,
          textRight: true,
          size: 'sm',
        },
      ],
      key: summaryTableRow.key,
    }));

  const totalAmountPaidData = useMemo(
    () => ({
      summary: {
        detailsTotalAmount: {
          value: detailsTotalAmountValue ?? 0,
        },
        taxAmount: { value: taxAmountValue ?? 0 },
        totalAmount: { value: totalAmountValue ?? 0 },
        withholdingIncomeTaxValue: {
          value: withholdingIncomeTaxValue ?? 0,
        },
      },
      details: { rows: exciseRows, headers: exciseHeaders },
    }),
    [
      exciseHeaders,
      exciseRows,
      taxAmountValue,
      totalAmountValue,
      detailsTotalAmountValue,
      withholdingIncomeTaxValue,
    ]
  );

  return (
    <TotalAmountPaid
      className={cx(styles['container'])}
      {...totalAmountPaidData}
      hasWithholdingIncomeTax={hasWithholdingIncomeTax}
      isStacked
      align='right'
    />
  );
};
